import { useState } from "react";
import { ReactComponent as CollapseIcon } from "../../../images/box-collapser.svg";
import { ReactComponent as ArrowIcon } from "../../../images/arrow-small-left.svg";
import { ReactComponent as DownloadIcon } from "../../../images/download.svg";
import Table from "components/elements/Table";
import Select from "components/elements/Select";
import IconInput from "components/elements/IconInput";
import {
  USER_BUSINESS_ADMIN,
  USER_SUPERADMIN,
  CLIENT_AGENCY,
  CLIENT_ADVERTISER,
  USER_CLIENT_ENDUSER,
  CLIENT_BROADCASTER,
} from "core/Constants";
import { useOutletContext } from "react-router-dom";
import DateInput from "components/elements/DateInput";
import utility from "core/Utils";
import Modal from "components/elements/Modal";
import { useEffect } from "react";
import dataProvider from "core/DataProvider";
import { toast } from "react-hot-toast";
import SummaryExporter from "components/elements/SummaryExporter";

const headerMap = {
  brandname: "Brand Name",
  brandcategory: "Category",
  channel: "Channel Name",
  spots: "Total Spots",
};

const exportHeaders = [
  { label: "BRANDNAME", key: "brandname" },
  { label: "CATEGORY", key: "brandcategory" },
  { label: "CHANNELNAME", key: "channel" },
  { label: "TOTALSPOT", key: "spots" },
];

const SummaryReport = () => {
  const [spotCount, setSpotCount] = useState(0);
  const [channelCount, setChannelCount] = useState(0);
  const [advertiserCount, setAdvertiserCount] = useState(0);
  const [brandCount, setBrandCount] = useState(0);
  const [data, setData] = useState([]);
  const [fileID, setFileID] = useState(null);
  const [exportFormat, setExportFormat] = useState({
    value: "csv",
    label: "CSV",
  });

  const [commonSegmentCollapsed, setCommonSegmentCollapsed] = useState(false);
  const [formCollapsed, setFormCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const [columns, _] = useState(utility.optionFromObject(headerMap));
  const [selectedColumns, setSelectedColumns] = useState(
    utility.optionFromObject(headerMap)
  );
  const [showColModal, setShowColModal] = useState(false);
  const [search, setSearch] = useState("");
  const [performSearch, setPerformSearch] = useState(false);

  const [group, setGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupDetails, setGroupDetails] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [advertiser, setAdvertiser] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [brand, setBrand] = useState(null);
  const [brands, setBrands] = useState([]);
  const [channel, setChannel] = useState(null);
  const [channels, setChannels] = useState([]);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  const token = useOutletContext();

  const handleSearchKeyDown = e => {
    if (e.keyCode == 13) {
      setPerformSearch(true);
    }
  };

  const getGroups = async () => {
    let d = await dataProvider.network.get("min-master/groups");
    if (d) {
      for (let i = 0; i < d.length; i++) {
        d[i] = { value: d[i].id, label: d[i].name };
      }

      utility.sortBy(d, "label");

      setGroups(d);
    } else {
      toast.error("Failed to load groups");
    }
  };

  const getGroupDetails = async () => {
    try {
      let d = await dataProvider.network.get("group?id=" + group.value);
      setGroupDetails(d);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch group details");
    }
  };

  const getClients = async () => {
    let d = await dataProvider.network.get(
      "min-master/clients?group-id=" + group.value
    );
    if (d) {
      d = d.map(x => {
        return { label: x.name, value: x.id };
      });

      utility.sortBy(d, "label");

      setClients(d);
    } else {
      alert("failed to fetch clients");
    }
  };

  const getAdvertisers = async () => {
    try {
      let advs = await dataProvider.network.get(
        "report/advertisers/" + client.value
      );
      advs = advs.map(x => ({ value: x.id, label: x.name }));

      utility.sortBy(advs, "label");

      setAdvertisers(advs);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load advertisers");
    }
  };

  const getCategories = async () => {
    if (token.type === USER_CLIENT_ENDUSER) {
      try {
        let cats = await dataProvider.network.post(`min-master/brandcategory`, {
          advertisers: [advertiser.value],
        });
        cats = cats.map(x => ({
          label: x.name,
          value: x.id,
        }));

        utility.sortBy(cats, "label");

        setCategories(cats);
      } catch (error) {
        console.error(error);
        toast.error("Failed to get categories");
      }
    } else {
      try {
        let cats = await dataProvider.network.post(`masters/brand-category`, {
          advertisers: [advertiser.value],
        });
        cats = cats.map(x => ({
          label: x.brandcategory,
          value: x.brandcategorycode,
        }));

        setCategories(cats);
      } catch (error) {
        console.error(error);
        toast.error("Failed to get categories");
      }
    }
  };

  const getBrands = async () => {
    try {
      let brands = await dataProvider.network.post(`min-master/brand`, {
        advertisers: [parseInt(advertiser.value)],
        categories: category.map(x => parseInt(x.value)),
      });
      brands = brands.map(x => ({ label: x.name, value: x.id }));

      utility.sortBy(brands, "label");

      setBrands(brands);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load brands");
    }
  };

  const getChannels = async () => {
    try {
      let channels = await dataProvider.network.get(`min-master/channels`);
      channels = channels.map(x => ({ value: x.id, label: x.name }));

      utility.sortBy(channels, "label");

      setChannels(channels);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch channels");
    }
  };

  const submit = async e => {
    e.preventDefault();

    // validations
    try {
      if (!token.client_id) {
        // internal users
        if (!group) {
          throw new Error("Please select group");
        }
        if (!client) {
          throw new Error("Please select client");
        }
        if (!periodFrom) {
          throw new Error("Please select from date");
        }
        if (!periodTo) {
          throw new Error("Please select to date");
        }

        if (groupDetails && groupDetails.client_type === CLIENT_BROADCASTER) {
          if (!channel) {
            throw new Error("Please select channel");
          }
        } else if (!advertiser) {
          throw new Error("Please select advertiser");
        }
      } else {
        // client users
        // for advertiser/agency
        if ([CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)) {
          if (!periodFrom) {
            throw new Error("Please select from date");
          }
          if (!periodTo) {
            throw new Error("Please select to date");
          }
          if (!advertiser) {
            throw new Error("Please select advertiser");
          }
        }

        // for agency, broadcaster
        if ([CLIENT_BROADCASTER].includes(token.client_type)) {
          if (!periodFrom) {
            throw new Error("Please select from date");
          }
          if (!periodTo) {
            throw new Error("Please select to date");
          }
          if (!channel) {
            throw new Error("Please select channel");
          }
        }
      }
    } catch (error) {
      toast.error(error.message);
      return;
    }

    setLoading(true);

    let data = {
      client_id: client.value,
      period_from: periodFrom,
      period_to: periodTo,
      advertiser: advertiser ? advertiser.value : 0,
      categories: category && category.length ? category.map(x => x.value) : [],
      brands: brand && brand.length ? brand.map(x => x.value) : [],
      channels: channel && channel.length ? channel.map(x => x.value) : [],
    };

    try {
      let resp = await dataProvider.network.post("summary-report", data);
      let records = resp.data;

      setFileID(resp.file_id);
      setData(records);
      setFormCollapsed(true);

      let spotCount = records.length;
      let channels = [];
      let brands = [];

      for (let i = 0; i < records.length; i++) {
        if (!channels.includes(records[i].channel)) {
          channels.push(records[i].channel);
        }
        if (!brands.includes(records[i].brandname)) {
          brands.push(records[i].brandname);
        }
      }

      setSpotCount(spotCount);
      setChannelCount(channels.length);
      setBrandCount(brands.length);
      setAdvertiserCount(1);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load summary report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGroups();

    if (
      [CLIENT_ADVERTISER, CLIENT_AGENCY, CLIENT_BROADCASTER].includes(
        token.client_type
      )
    ) {
      setGroup({ value: token.group_id, label: "" });
      setClient({ value: token.client_id, label: "" });
    }
  }, []);

  useEffect(() => {
    if (group && group.value) {
      getClients();
      getChannels();
      getGroupDetails();
    }
  }, [group]);

  useEffect(() => {
    if (client && client.value) {
      setAdvertiser(null);
      getAdvertisers();
    }
  }, [client]);

  useEffect(() => {
    if (advertiser && advertiser.value) {
      getCategories();
    }
  }, [advertiser]);

  useEffect(() => {
    if (category && category.length) {
      getBrands();
    }
  }, [category]);

  const permissionCheck = key => {
    let show = false;

    if (key === "advertiser_select") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "advertiser_count") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "brand_count") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "brand_select") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "category_select") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (key === "extra_segment") {
      if (
        [USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type) ||
        [CLIENT_ADVERTISER, CLIENT_AGENCY].includes(token.client_type)
      ) {
        show = true;
      }
    }

    if (["group_select", "client_select"].includes(key)) {
      if ([USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type)) {
        show = true;
      }
    }

    return show;
  };

  const colModal = (
    <Modal onClose={() => setShowColModal(false)}>
      <h3>Select Columns</h3>
      <div className="full-row" style={{ width: "50vw", marginTop: "2rem" }}>
        <Select
          isMulti
          value={selectedColumns}
          options={columns}
          placeholder="Select columns to show"
          onChange={val => setSelectedColumns(val)}
        />
      </div>
    </Modal>
  );

  let filteredData = [];
  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      let toPush = true;

      if (performSearch && search.length > 2) {
        let searchableString = "";
        let searchString = search.toLowerCase();
        let keys = Object.keys(data[i]);

        for (let j = 0; j < keys.length; j++) {
          if (typeof data[i][keys[j]] == "string") {
            searchableString += data[i][keys[j]].toLowerCase();
          }

          if (typeof data[i][keys[j]] == "number") {
            searchableString += String(data[i][keys[j]]);
          }
        }

        if (!searchableString.includes(searchString)) {
          toPush = false;
        }
      }

      if (toPush) {
        filteredData.push(data[i]);
      }
    }
  }

  const now = new Date();
  const datePart = now.toISOString().split("T")[0].replaceAll(/\-/g, "");
  const timePart = now.toTimeString().split(" ")[0].replaceAll(/\:/g, "");
  const random = utility.getRandomNumbers(10000, 99999);

  const fileName = `${datePart}${timePart}_${periodFrom}_${periodTo}_${random}`;

  return (
    <section id="summary-report">
      {showColModal && colModal}
      <div className="grey-box">
        <div className="left">
          <h3>Summary Report</h3>
          <h4>Reports &gt; Summary Report</h4>
        </div>
        <div className="right">
          {loading && (
            <div className="loader">
              <div className="lds-hourglass"></div>
            </div>
          )}
          <div className="stat">
            <span>{spotCount}</span>
            <span>Spot Count</span>
          </div>
          <div className="stat">
            <span>{channelCount}</span>
            <span>Channels</span>
          </div>
          {permissionCheck("advertiser_count") && (
            <div className="stat">
              <span>{advertiserCount}</span>
              <span>Advertiser</span>
            </div>
          )}
          {[USER_BUSINESS_ADMIN, USER_SUPERADMIN].includes(token.type) &&
            [CLIENT_AGENCY, CLIENT_ADVERTISER].includes(token.client_type) && (
              <div className="stat">
                <span>{brandCount}</span>
                <span>Brand</span>
              </div>
            )}
        </div>
      </div>
      <div className="flex mtop">
        <div
          className={formCollapsed ? "form-wrapper collapsed" : "form-wrapper"}
        >
          <div
            className={
              commonSegmentCollapsed ? "form-segment collapsed" : "form-segment"
            }
          >
            <div
              className="segment-head"
              onClick={e => setCommonSegmentCollapsed(!commonSegmentCollapsed)}
            >
              <h6>Common</h6>
              <span>
                <CollapseIcon />
              </span>
            </div>
            <div className="segment-body">
              {permissionCheck("group_select") && (
                <Select
                  options={groups}
                  value={group}
                  onChange={val => setGroup(val)}
                  placeholder="Group"
                />
              )}
              {permissionCheck("client_select") && (
                <Select
                  options={clients}
                  value={client}
                  onChange={val => setClient(val)}
                  placeholder="Client"
                />
              )}
              <DateInput
                value={periodFrom}
                onChange={val => setPeriodFrom(val)}
                placeholder="Period from"
              />
              <DateInput
                value={periodTo}
                onChange={val => setPeriodTo(val)}
                placeholder="Period to"
              />
              {permissionCheck("advertiser_select") && (
                <Select
                  options={advertisers}
                  value={advertiser}
                  onChange={val => setAdvertiser(val)}
                  placeholder="Advertiser"
                />
              )}
              {permissionCheck("category_select") && (
                <Select
                  isMulti
                  options={categories}
                  value={category}
                  onChange={val => setCategory(val)}
                  placeholder="Category"
                />
              )}
              {permissionCheck("brand_select") && (
                <Select
                  isMulti
                  options={brands}
                  value={brand}
                  onChange={val => setBrand(val)}
                  placeholder="Brand"
                />
              )}
              <Select
                isMulti
                options={channels}
                value={channel}
                onChange={val => setChannel(val)}
                placeholder="Channel"
              />
            </div>
          </div>
          <button onClick={submit}>Submit</button>
          <div
            className="collapser"
            onClick={e => setFormCollapsed(!formCollapsed)}
          >
            <ArrowIcon />
          </div>
        </div>
        <div className="results-wrapper flex-full">
          <div className="head flex flex-v-center">
            <h3>Results</h3>
            <div className="right">
              <div className="flex flex-v-center">
                <div className="el">
                  <input
                    type="text"
                    readOnly
                    placeholder="Select Columns"
                    onClick={e => {
                      e.preventDefault();
                      setShowColModal(true);
                    }}
                  />
                </div>
                <div className="el">
                  <IconInput
                    value={search}
                    onKeyDown={handleSearchKeyDown}
                    onChange={e => {
                      setPerformSearch(false);
                      setSearch(e.target.value);
                    }}
                    placeholder="Search"
                    icon="search"
                  />
                </div>
                <div className="el">
                  <Select
                    value={exportFormat}
                    onChange={val => {
                      setExportFormat(val);
                    }}
                    options={[
                      { value: "csv", label: "CSV" },
                      { value: "xlsx", label: "XLSX" },
                      { value: "pdf", label: "PDF" },
                    ]}
                    placeholder="Format"
                  />
                </div>
                <SummaryExporter
                  format={exportFormat.value}
                  data={data}
                  headers={exportHeaders}
                  fileID={fileID}
                  filename={`${fileName}.${exportFormat.value}`}
                  clientID={
                    token.client_id
                      ? token.client_id
                      : client && client.value
                      ? client.value
                      : null
                  }
                />
              </div>
            </div>
          </div>
          <div className="results">
            <Table
              headers={utility.objectFromOptions(selectedColumns)}
              data={filteredData}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SummaryReport;
